import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  inject,
  Input,
  OnInit,
} from '@angular/core';
import { isDefined } from '@sama/js-core';
import { DirectiveSuperclass } from '@sama/ng-core';
import { withHistory } from '@sama/rxjs-core';
import { isUndefined, round } from 'lodash';
import { BehaviorSubject, Observable, timer } from 'rxjs';
import { filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { MouseEventService } from '../../../cross-team-shared/misc-services/events/mouse-event.service';
import { SideViewCamerasService } from '../../render/cameras/side-view-cameras.service';
import { RenderTriggerService } from '../../render/render-trigger.service';
import { PointCloudContentStore } from '../../state/point-cloud-content-store';
import { getFaceDimensions } from '../../utils/cuboid-utils';
import { mapPoint } from '../../utils/point-utils';
import { EditPanelCursorService } from './edit-panel-cursor.service';
import { EditPanelService } from './edit-panel.service';

export type Axis = 0 | 1 | 2;

const titles = {
  2: 'top',
  1: 'back',
  0: 'side',
};

const icons = {
  2: 'yaw',
  1: 'roll',
  0: 'pitch',
};

@Component({
  selector: 'h3-edit-panel',
  templateUrl: './edit-panel.component.html',
  styleUrls: ['./edit-panel.component.scss'],
  providers: [EditPanelService, EditPanelCursorService, MouseEventService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class EditPanelComponent
  extends DirectiveSuperclass
  implements OnInit, AfterViewInit
{
  @Input({ required: true }) axis!: Axis;

  private readonly sideViewCamerasService = inject(SideViewCamerasService);
  private readonly store = inject(PointCloudContentStore);
  private readonly renderTriggerService = inject(RenderTriggerService);
  private readonly mouseEventService = inject(MouseEventService);
  private readonly editPanelService = inject(EditPanelService);
  private readonly editPanelCursorService = inject(EditPanelCursorService);
  private readonly elementRef = inject(ElementRef);

  protected shouldHighlightWidthDimension$ = new BehaviorSubject(false);
  protected shouldHighlightHeightDimension$ = new BehaviorSubject(false);
  protected isLeftEditPanel$ = this.store('editPanel')('location').$.pipe(
    map((location) => location === 'left'),
  );
  protected title$: Observable<string> = this.getInput$('axis').pipe(
    map((axis) => titles[axis]),
  );
  protected icon$: Observable<string> = this.getInput$('axis').pipe(
    map((axis) => icons[axis]),
  );
  protected dimensions$ = this.store.focusedShapeLocation$.pipe(
    filter(isDefined),
    map((cuboid) =>
      mapPoint(getFaceDimensions(cuboid.scale, this.axis), (val) =>
        round(val, 2),
      ),
    ),
  );

  @HostListener('wheel', ['$event'])
  protected handleMouseEvent(event: WheelEvent) {
    event.preventDefault();

    const dollyFactor = -Math.sign(event.deltaY) * 0.1;
    this.sideViewCamerasService.dollyCamera(this.axis, dollyFactor);
    this.renderTriggerService.trigger();
  }

  @HostListener('mousemove', ['$event'])
  @HostListener('mouseup', ['$event'])
  @HostListener('mousedown', ['$event'])
  @HostListener('click', ['$event'])
  @HostListener('mouseout', ['$event'])
  protected handleMouseEvents(event: MouseEvent) {
    if (this.store.state().readOnly) {
      return;
    }

    this.mouseEventService.handle({ target: 'container', event });
  }

  ngOnInit() {
    this.store
      .getViewRegionStore(this.axis)('outputName')
      .set(this.store.state().output.name);
    this.subscribeToFaceDimensionResize();
  }

  ngAfterViewInit() {
    if (this.store.state().readOnly) {
      return;
    }

    this.editPanelService.setAxis(this.axis);
    this.editPanelCursorService.install(this.elementRef, this.axis);
  }

  private subscribeToFaceDimensionResize() {
    this.subscribeTo(
      this.store.focusedShape$.pipe(
        withLatestFrom(this.dimensions$),
        withHistory(1),
        filter(([[currentShape], previous]) => {
          const previousShape = previous && previous[0];
          return currentShape?.index === previousShape?.index;
        }),
        tap(([[_, currentDimensions], previous]) => {
          const previousDimensions = previous && previous[1];

          if (isUndefined(previousDimensions)) {
            return;
          }

          if (Math.abs(currentDimensions[0] - previousDimensions[0]) > 0.01) {
            this.shouldHighlightWidthDimension$.next(true);
          }

          if (Math.abs(currentDimensions[1] - previousDimensions[1]) > 0.01) {
            this.shouldHighlightHeightDimension$.next(true);
          }
        }),
        switchMap(() => timer(500)),
      ),
      () => {
        this.shouldHighlightHeightDimension$.next(false);
        this.shouldHighlightWidthDimension$.next(false);
      },
    );
  }
}
